import { useContext, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useGetOrdersQuery } from 'redux/api/ordersApi';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import {
  getCurrentPage,
  getSize,
  setTableCurrentPage,
  setTablePagination,
} from 'redux/slices/tableConfig';

import { LayoutContext } from 'contexts';

import { formatSeasonLabel } from 'helpers/formatSeason';

import { useColumns } from './columns.data';
import { useOrderFilters } from './filters.data';

const useList = () => {
  const { breadcrumbSet, breadcrumbSecondarySet } = useContext(LayoutContext);

  const [customerCode, customerCodeSet] = useState<string>('');

  const columns = useColumns();
  const filters = useOrderFilters();
  const { t } = useTranslation();

  const { register, setValue, handleSubmit } = useForm();

  const dispatch = useAppDispatch();
  const page = useAppSelector(getCurrentPage);
  const size = useAppSelector(getSize);
  const { season, zone, marketLine } = useAppSelector((state) => state.settings);

  // ─── RTK Query: paginated orders list (cached, auto-refetches on param change)
  const { data: ordersResponse, isLoading: isOrdersLoading } = useGetOrdersQuery(
    {
      page,
      size,
      ...(customerCode ? { customerCode } : {}),
      ...(zone ? { zone } : {}),
      ...(season ? { season } : {}),
      ...(marketLine ? { marketingLine: marketLine } : {}),
    },
    {
      // Refetch when we come back to this page (stale data auto-updates in background)
      refetchOnMountOrArgChange: 30, // 30 seconds staleness threshold
    }
  );

  const ordersData = ordersResponse?.content ?? [];

  // Update pagination metadata from response
  useEffect(() => {
    if (ordersResponse) {
      dispatch(setTablePagination(ordersResponse.totalPages ?? 0));
    }
  }, [ordersResponse, dispatch]);

  const onSubmit = (filtersData: FieldValues) => {
    const nextCode = (filtersData.customerCode ?? '').trim();

    if (nextCode === customerCode) return;

    dispatch(setTableCurrentPage(0));
    customerCodeSet(nextCode);
  };

  useEffect(() => {
    const breadcrumb = [
      {
        label: t('nav.manageOrders'),
      },
    ];

    breadcrumbSet(breadcrumb);
  }, [t]);

  useEffect(() => {
    dispatch(setTableCurrentPage(0));
  }, [dispatch]);

  useEffect(() => {
    if (season) {
      const breadcrumbSecondary = [
        {
          label: formatSeasonLabel(season),
        },
        {
          label: t('nav.viewOrders'),
        },
      ];

      breadcrumbSecondarySet(breadcrumbSecondary);
    }
  }, [season, t]);

  return {
    columns,
    filters,
    register,
    setValue,
    onSubmit,
    handleSubmit,
    ordersData,
    isOrdersLoading,
    customerCode,
  };
};

export { useList };
